import { axiosClientAdmin } from '@/lib/auth';
import { AxiosError } from 'axios';
import { Comparator, Filter, FilterGroup, FilterOperator, SortOrder } from "../types.gen";
import { UseInfiniteQueryOptions, UseQueryOptions } from '@tanstack/react-query';

export enum EnumProjectAction {
  Viewed = 'Viewed',
  ContactAdded = 'ContactAdded',
  ArmStateChanged = 'ArmStateChanged',
  ArmProfileChanged = 'ArmProfileChanged',
  SirenProfileChanged = 'SirenProfileChanged',
  NoThreatDispositionChanged = 'NoThreatDispositionChanged',
  MonitoringZoneUpdated = 'MonitoringZoneUpdated',
  NameChanged = 'NameChanged',
  CompanyChanged = 'CompanyChanged',
  NewMonitoringAlertsAfterSecondsChanged = 'NewMonitoringAlertsAfterSecondsChanged',
  SirenCooldownSecondsChanged = 'SirenCooldownSecondsChanged',
  SirensUnlinkedChanged = 'SirensUnlinkedChanged',
  GuardResponseEnabledChanged = 'GuardResponseEnabledChanged',
  GuardResponseDispatchCodeChanged = 'GuardResponseDispatchCodeChanged',
  ComplianceEnabledChanged = 'ComplianceEnabledChanged',
  SiteNotesChanged = 'SiteNotesChanged',
  AiAutomationLevelChanged = 'AiAutomationLevelChanged',
  AiPrimerChanged = 'AiPrimerChanged',
  NoThreatAiPrimerChanged = 'NoThreatAiPrimerChanged',
  TimeZoneChanged = 'TimeZoneChanged',
  AddressChanged = 'AddressChanged',
  DetectionLabelsChanged = 'DetectionLabelsChanged',
  ExternalServiceOptionsChanged = 'ExternalServiceOptionsChanged',
  ArchivedAtChanged = 'ArchivedAtChanged',
  GridChanged = 'GridChanged'
}

export interface ProjectAuditLogSearched {
  success?: boolean;
  message?: string;
  code?: string;
  errors?: string[];
  totalRows?: number;
  rows?: ProjectAuditLogDto[];
  nextPage?: number;
}

export interface ProjectAuditLogDto {
  id: string;
  action?: EnumProjectAction;
  description?: string;
  occurred?: string;
  userName?: string;
}

export type SearchProjectAuditLogInput = {
  pageNumber?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: SortOrder;
  filterOperator?: FilterOperator;
  filterFilters?: Filter[];
  filterFilterGroups?: FilterGroup[];

  // Explicit filters
  projectId?: string;
  action?: EnumProjectAction;
  search?: string;
}

export const searchProjectAuditLog = async (
  params: SearchProjectAuditLogInput = {}
) => {
  try {
    const requestParams = {
      PageNumber: params.pageNumber,
      PageSize: params.pageSize,
      SortBy: params.sortBy ?? "occurred",
      SortDir: params.sortDir ?? SortOrder.Descending,
      'Filter.Operator': params.filterOperator ?? FilterOperator.And,
      'Filter.Filters': params.filterFilters ?? [],
      'Filter.FilterGroups': params.filterFilterGroups ?? [],
    };

    if (params.projectId) {
      requestParams['Filter.Filters'].push({
        action: Comparator.Eq,
        field: 'projectId',
        value: params.projectId,
      });
    }

    if (params.action) {
      requestParams['Filter.Filters'].push({
        action: Comparator.Eq,
        field: 'action',
        value: params.action,
      });
    }

    if (params.search) {
      requestParams['Filter.Filters'].push({
        action: Comparator.ILike,
        field: 'description',
        value: params.search,
      });
    }

    const response = await axiosClientAdmin.get<ProjectAuditLogSearched>(
      '/Monitoring/project/audit/search',
      {
        params: requestParams,
      }
    );

    if (!response.data) {
      throw new Error('No data received from server');
    }

    const totalRows = response.data.totalRows ?? 0;

    return {
      ...response.data,
      totalRows,
      nextPage: params?.pageNumber ? params.pageNumber + 1 : 0,
    };
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    if (axiosError?.response) {
      throw new Error(axiosError.response.data?.message || 'Failed to search project audit logs');
    }
    throw new Error('Failed to search project audit logs');
  }
};

export const SearchProjectAuditLogQuery = (input: SearchProjectAuditLogInput) => {
  return {
    queryKey: ['projectAuditLog', input],
    queryFn: () => searchProjectAuditLog(input),
  } satisfies UseQueryOptions;
}

export const SearchProjectAuditLogInfiniteQuery = (input: SearchProjectAuditLogInput) => {
  return {
    queryKey: ['projectAuditLog/infinite', input],
    queryFn: () => searchProjectAuditLog(input),
    getPreviousPageParam: (firstPage: ProjectAuditLogSearched) => firstPage?.nextPage ? firstPage.nextPage - 1 : undefined,
    getNextPageParam: (lastPage: ProjectAuditLogSearched) => lastPage?.nextPage,
    initialPageParam: 1,
  } satisfies UseInfiniteQueryOptions<ProjectAuditLogSearched, Error>;
}